import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '../../context/ThemeContext';
import {
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Clock,
  Compass,
  ChefHat,
  Sofa,
  Layers,
  Wrench,
  Briefcase,
  Calendar,
  Layout,
  Home,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Unified Service specifications and structural data
interface ServiceDetail {
  id: string;
  name: string;
  badge: string;
  desc: string;
  inclusions: string[];
  timeline: { step: string; duration: string }[];
  metrics: { label: string; value: string; icon: any }[];
  image: string;
}

const SERVICE_CATEGORIES: Record<string, {
  label: string;
  description: string;
  icon: any;
  hotspots: { id: string; label: string; x: string; y: string }[];
  details: Record<string, ServiceDetail>;
}> = {
  "home-interiors": {
    label: "Home Interiors",
    description: "Complete luxury 2BHK, 3BHK & villa transformations.",
    icon: Home,
    hotspots: [
      { id: "kitchen", label: "Modular Kitchen", x: "25%", y: "65%" },
      { id: "living", label: "Living Room & TV Wall", x: "50%", y: "45%" },
      { id: "wardrobe", label: "Wardrobes & Closets", x: "75%", y: "60%" },
      { id: "ceiling", label: "Ceiling & Profile Lights", x: "48%", y: "20%" }
    ],
    details: {
      "kitchen": {
        id: "kitchen",
        name: "Modular Kitchens",
        badge: "German Finish • Ergonomic Design",
        desc: "German-precision engineering optimized for traditional Indian cooking. Engineered with water-resistant materials, seamless handles, and smart storage.",
        inclusions: ["L-Shaped & U-Shaped layouts", "Boiling Water Resistant (BWR) Marine Plywood", "Premium anti-scratch acrylic & laminate finishes", "Soft-close tandem drawers & lift-up cabinets"],
        timeline: [
          { step: "Design & 3D Render", duration: "10 Days" },
          { step: "Factory Manufacturing", duration: "25 Days" },
          { step: "Installation & Countertop", duration: "10 Days" }
        ],
        metrics: [
          { label: "Warranty", value: "10 Years", icon: ShieldCheck },
          { label: "Delivery", value: "45 Days", icon: Clock },
          { label: "Hardware", value: "Hettich / Blum", icon: Compass }
        ],
        image: "/images/projects/kmv-vivaan/KMV(1).jpg"
      },
      "living": {
        id: "living",
        name: "Living Room & TV Walls",
        badge: "Bespoke Lounges • Premium Accents",
        desc: "The heart of your home, designed to stun. Custom modular TV consoles, Italian marble backdrops, wooden rafters, and integrated display shelving.",
        inclusions: ["Custom media units with hidden cable organizers", "Fluted wall panels & marble textured finishes", "Bespoke L-shape fabric sofas & accent lounge chairs", "Concealed ambient backlight arrangements"],
        timeline: [
          { step: "Conceptual Sketching", duration: "5 Days" },
          { step: "Material Sourcing & Framing", duration: "15 Days" },
          { step: "On-site Carpentry & Finish", duration: "15 Days" }
        ],
        metrics: [
          { label: "Finishes", value: "Veneer / PU Paint", icon: ShieldCheck },
          { label: "Crafting", value: "35 Days", icon: Clock },
          { label: "Aesthetics", value: "Custom Built", icon: Compass }
        ],
        image: "/images/projects/my-home-abhra/MH Abhra(12).jpg"
      },
      "wardrobe": {
        id: "wardrobe",
        name: "Wardrobes & Storage Solutions",
        badge: "Floor-to-Ceiling • Maximum Volume",
        desc: "High-capacity smart storage tailored to your daily routines. Smooth sliding panels, soft-close hinged wardrobes, and customized walk-in closets.",
        inclusions: ["Heavy-duty sliding systems & lacquer glass options", "Dedicated jewelry drawers & built-in sensor lights", "Ergonomic hanging sections & integrated study unit attachments", "Anti-fingerprint matte laminates"],
        timeline: [
          { step: "Internal Layout Plan", duration: "5 Days" },
          { step: "Precision Carcass Cutting", duration: "20 Days" },
          { step: "Assembly & Alignment", duration: "10 Days" }
        ],
        metrics: [
          { label: "Hinges", value: "Soft-Close Only", icon: ShieldCheck },
          { label: "Setup Time", value: "30 Days", icon: Clock },
          { label: "Ply Thickness", value: "18mm Core", icon: Compass }
        ],
        image: "/images/projects/prestige-beverly-hills/FB_IMG_1778706421090.jpg"
      },
      "ceiling": {
        id: "ceiling",
        name: "Ceilings & Architectural Lighting",
        badge: "Warm Ambience • Modern Geometries",
        desc: "Transform your home's mood with customized ceiling designs. Layered gypsum boards, elegant wooden rafters, and micro-LED profile lighting.",
        inclusions: ["Saint-Gobain gypsum boards for fire & crack resistance", "Integrated LED profile channel layouts", "Magnetic tracks & dimmable spotlight fittings", "Teakwood/charcoal panel highlights"],
        timeline: [
          { step: "Framing & Wiring Layout", duration: "7 Days" },
          { step: "Gypsum Fixing & Channel Placement", duration: "10 Days" },
          { step: "Painting & Fixture Mounting", duration: "8 Days" }
        ],
        metrics: [
          { label: "Brands", value: "Saint-Gobain / Philips", icon: ShieldCheck },
          { label: "Installation", value: "25 Days", icon: Clock },
          { label: "Efficiency", value: "Low-Voltage LEDs", icon: Compass }
        ],
        image: "/images/projects/rajpushpa-provincia/Provincia(2).jpg"
      }
    }
  },
  "commercial": {
    label: "Commercial Spaces",
    description: "Highly productive, premium corporate, retail and cafe layouts.",
    icon: Briefcase,
    hotspots: [
      { id: "reception", label: "Reception & Lounge", x: "30%", y: "60%" },
      { id: "workspace", label: "Workstations & Hubs", x: "65%", y: "50%" },
      { id: "cabins", label: "Executive Glass Cabins", x: "78%", y: "30%" }
    ],
    details: {
      "reception": {
        id: "reception",
        name: "Reception & Lounge Areas",
        badge: "Brand Identity • High First Impression",
        desc: "Sleek, brand-aligned entrance areas that captivate visitors immediately. Features premium solid-surface counters, biophilic wall elements, and lounge seating.",
        inclusions: ["Custom Corian solid-surface reception counters", "Integrated back-lit metal brand logos", "Green wall planters & preserved moss backdrops", "Designer accent chairs & custom terrazzo floors"],
        timeline: [
          { step: "3D Brand Modelling", duration: "7 Days" },
          { step: "Counter Fabrication", duration: "18 Days" },
          { step: "On-site Assembly & Lighting", duration: "10 Days" }
        ],
        metrics: [
          { label: "Materials", value: "Solid Surface / Moss", icon: ShieldCheck },
          { label: "Execution", value: "35 Days", icon: Clock },
          { label: "Customization", value: "Fully Custom", icon: Compass }
        ],
        image: "/images/projects/office/tina-roy-ROBCVXIoXkc-unsplash.jpg"
      },
      "workspace": {
        id: "workspace",
        name: "Ergonomic Workstations",
        badge: "High Productivity • Optimized Flow",
        desc: "Scalable modular benching networks with clean cable-management, power hubs, and acoustical partition panels to foster teamwork and focus.",
        inclusions: ["Heavy-duty metal understructure with powder coating", "Acoustic desktop divider screens", "Integrated soft-wiring tracks & pop-up sockets", "Bespoke linear overhead strip LED grids"],
        timeline: [
          { step: "Grid Layout Planning", duration: "5 Days" },
          { step: "Modular Manufacturing", duration: "20 Days" },
          { step: "Wiring & Structural Assembly", duration: "12 Days" }
        ],
        metrics: [
          { label: "Acoustics", value: "Fabric Dividers", icon: ShieldCheck },
          { label: "Lead Time", value: "37 Days", icon: Clock },
          { label: "Framework", value: "Mild Steel / Modular", icon: Compass }
        ],
        image: "/images/projects/office/apa-singh-Hx8e7lW9BXI-unsplash.jpg"
      },
      "cabins": {
        id: "cabins",
        name: "Executive Glass Cabins",
        badge: "Sound Insulated • Sleek Corporate Layout",
        desc: "Premium cabins prioritizing transparency and acoustic confidentiality. Heavy-gauge aluminum framing with double-glazed glass panels.",
        inclusions: ["12mm toughened glass panels or double-glazing", "Soundproof solid flush wood cabin back walls", "Custom veneer veneer executive desks", "Concealed air-con venting integrations"],
        timeline: [
          { step: "Acoustic Mapping", duration: "5 Days" },
          { step: "Glass & Profile Slicing", duration: "15 Days" },
          { step: "Glazing & Desk Carpentry", duration: "15 Days" }
        ],
        metrics: [
          { label: "Decibels", value: "STC-35 Insulated", icon: ShieldCheck },
          { label: "Sourcing", value: "35 Days", icon: Clock },
          { label: "Glazing", value: "Double / Toughened", icon: Compass }
        ],
        image: "/images/projects/office/pooja-singh-R0l_Xf6iV1E-unsplash.jpg"
      }
    }
  },
  "turnkey": {
    label: "Turnkey Projects",
    description: "End-to-end designers, suppliers, and builders under one contract.",
    icon: Calendar,
    hotspots: [
      { id: "concept", label: "1. Design & Approval", x: "20%", y: "40%" },
      { id: "procure", label: "2. Procurement & Masonry", x: "45%", y: "55%" },
      { id: "handover", label: "3. Installation & Handover", x: "75%", y: "45%" }
    ],
    details: {
      "concept": {
        id: "concept",
        name: "1. Concept & 3D Visualization",
        badge: "Zero Ambiguity Design",
        desc: "We align on layouts, premium finishes, and provide hyper-realistic 3D walkthrough rendering plans so you know exactly what your home looks like.",
        inclusions: ["Interactive 2D space allocation drafts", "High-fidelity photorealistic 3D interior renderings", "Detailed material specification charts", "Detailed architectural MEP plans (electrical/plumbing)"],
        timeline: [
          { step: "Site Measurement", duration: "3 Days" },
          { step: "Layout Finalization", duration: "7 Days" },
          { step: "3D Rendering & Approval", duration: "10 Days" }
        ],
        metrics: [
          { label: "Walkthroughs", value: "Full 3D VR Option", icon: ShieldCheck },
          { label: "Approval", value: "20 Days", icon: Clock },
          { label: "Renders", value: "Unrestricted Iterations", icon: Compass }
        ],
        image: "/images/projects/rajpushpa-provincia/Provincia.jpg"
      },
      "procure": {
        id: "procure",
        name: "2. MEP Sourcing & Heavy Masonry",
        badge: "Under-one-roof execution",
        desc: "We coordinate civil alterations, electrical re-wiring, plumbing upgrades, and premium material purchasing. No third-party contractor headaches.",
        inclusions: ["Heavy-duty electrical layout adjustments", "Premium paint coats, wall tiling and structural modifications", "High-efficiency false ceiling gypsum layouts", "Factory-grade carpentry raw materials verification"],
        timeline: [
          { step: "Demolition & Masonry", duration: "10 Days" },
          { step: "Concealed MEP Wiring", duration: "15 Days" },
          { step: "Plastering & Ceiling Frame", duration: "15 Days" }
        ],
        metrics: [
          { label: "Quality Checks", value: "120-Point Audit", icon: ShieldCheck },
          { label: "Duration", value: "40 Days", icon: Clock },
          { label: "Coordination", value: "Dedicated PM", icon: Compass }
        ],
        image: "/images/projects/kmv-vivaan/KMV(11).jpg"
      },
      "handover": {
        id: "handover",
        name: "3. Precision Install & Clean Handover",
        badge: "Ready-to-live execution",
        desc: "Final assembly of modular furniture, appliance installations, customized lighting setups, detailed deep cleaning, and formal walkthrough handover.",
        inclusions: ["Factory modular carcass positioning & levelling", "Precision countertop mounting & appliance wiring", "Spotlight tuning & designer decor placement", "Deep industrial sanitize wash & final keys presentation"],
        timeline: [
          { step: "Modular Cabinet Install", duration: "12 Days" },
          { step: "Lights & Hardware Calibration", duration: "8 Days" },
          { step: "Deep Cleaning & Audit", duration: "5 Days" }
        ],
        metrics: [
          { label: "Handover", value: "Move-in Ready", icon: ShieldCheck },
          { label: "Final Stage", value: "25 Days", icon: Clock },
          { label: "Audit", value: "Snag-free Signoff", icon: Compass }
        ],
        image: "/images/projects/rajpushpa-regalia/FB_IMG_1778706469544.jpg"
      }
    }
  }
};

export function ServicesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // State managers
  const [activeCategory, setActiveCategory] = useState<string>("home-interiors");
  const [isHovered, setIsHovered] = useState(false);
  const currentCategoryData = SERVICE_CATEGORIES[activeCategory] || SERVICE_CATEGORIES["home-interiors"];

  // Set default active detail to the first hotspot in the new category
  const [activeDetailId, setActiveDetailId] = useState<string>(currentCategoryData.hotspots[0].id);

  // Safely retrieve detail
  const activeDetail = currentCategoryData.details[activeDetailId] || Object.values(currentCategoryData.details)[0];

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    const newCatData = SERVICE_CATEGORIES[catId];
    if (newCatData && newCatData.hotspots.length > 0) {
      setActiveDetailId(newCatData.hotspots[0].id);
    }
  };

  const hotspots = currentCategoryData.hotspots;

  const handleNext = () => {
    const currentIndex = hotspots.findIndex(spot => spot.id === activeDetailId);
    const nextIndex = (currentIndex + 1) % hotspots.length;
    setActiveDetailId(hotspots[nextIndex].id);
  };

  const handlePrev = () => {
    const currentIndex = hotspots.findIndex(spot => spot.id === activeDetailId);
    const prevIndex = (currentIndex - 1 + hotspots.length) % hotspots.length;
    setActiveDetailId(hotspots[prevIndex].id);
  };

  // Auto-play loop to automatically transition services details
  useEffect(() => {
    if (isHovered || hotspots.length <= 1) return;

    const timer = setInterval(() => {
      const currentIndex = hotspots.findIndex(spot => spot.id === activeDetailId);
      const nextIndex = (currentIndex + 1) % hotspots.length;
      setActiveDetailId(hotspots[nextIndex].id);
    }, 4000); // Transitions every 4.5s

    return () => clearInterval(timer);
  }, [activeDetailId, isHovered, hotspots]);

  const scrollToConsultation = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.querySelector('#consultation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Pre-select service if contact form supports it
      const selectElement = document.querySelector('select[name="service"]') as HTMLSelectElement;
      if (selectElement) {
        if (activeCategory === 'home-interiors') selectElement.value = 'Home Interiors';
        else if (activeCategory === 'commercial') selectElement.value = 'Commercial/Office Spaces';
        else selectElement.value = 'Turnkey Projects';
        // Dispatch event to update React state in form if needed
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  return (
    <section id="services" aria-label="Our interior design services" className={`py-20 sm:py-28 transition-colors duration-500 relative overflow-hidden ${isDark ? 'bg-[#0B0F1A]' : 'bg-bg-light'}`}>

      {/* Decorative Blur Backdrops */}
      <div className="absolute top-1/4 -left-36 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[100px] pointer-events-none opacity-40" />
      <div className="absolute bottom-1/4 -right-36 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px] pointer-events-none opacity-40" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 mb-4"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent">OUR SERVICES</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-3xl sm:text-4xl lg:text-[42px] font-poppins font-bold leading-tight mb-4 tracking-tight ${isDark ? 'text-white' : 'text-primary'}`}
          >
            Interior Design Services —{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-[#E0B880]">Everything Under One Roof</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-base sm:text-lg max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          >
            From concept to completion — we handle every detail so you don't have to.
          </motion.p>
        </div>

        {/* Level 1 Category Toggles */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10 sm:mb-16">
          {Object.entries(SERVICE_CATEGORIES).map(([catId, cat]) => {
            const Icon = cat.icon;
            const isActive = activeCategory === catId;
            return (
              <button
                key={catId}
                onClick={() => handleCategoryChange(catId)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${isActive
                    ? 'bg-accent text-white shadow-lg shadow-accent/25 font-bold border border-accent'
                    : isDark
                      ? 'bg-[#121A2E] text-gray-400 hover:text-white border border-white/5 hover:border-accent/30'
                      : 'bg-white text-gray-600 hover:text-primary border border-gray-100 hover:border-accent/20 shadow-sm'
                  }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-accent'}`} />
                {cat.label}
              </button>
            );
          })}

          {/* Extensible Open Tab "Coming Soon" */}
          {/* <div className="relative group">
            <button
              className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold tracking-wide border border-dashed transition-all duration-300 opacity-60 cursor-not-allowed ${
                isDark 
                  ? 'border-gray-700 bg-transparent text-gray-500' 
                  : 'border-gray-300 bg-transparent text-gray-400'
              }`}
            >
              <Wrench className="w-4 h-4 text-accent/50" />
              Custom Furniture & Decor
              <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-accent/20 text-accent ml-1 font-poppins">Coming Soon</span>
            </button>
          </div> */}
        </div>

        {/* Dual-Pane Dashboard */}
        <div
          className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >

          {/* Left Pane: Showhouse Blueprint Vector & Hotspots */}
          <div className="lg:col-span-6 flex flex-col justify-between">
            <div
              className={`rounded-3xl p-6 sm:p-8 flex-1 flex flex-col justify-center border relative min-h-[340px] sm:min-h-[420px] lg:min-h-[480px] overflow-hidden transition-all duration-300 ${isDark
                  ? 'bg-[#0E1626] border-white/[0.04]'
                  : 'bg-white border-black/[0.04] shadow-xl shadow-slate-100/40'
                }`}
            >

              {/* Graphical Technical Drawing Lines - Rendered as active room background render */}
              <div className="absolute inset-0 z-0 select-none pointer-events-none transition-all duration-700 ease-out">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeCategory + "-" + activeDetailId}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={activeDetail.image}
                      alt="3D Space Preview"
                      className="w-full h-full object-cover brightness-[0.85] contrast-[1.05]"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0E1626]/80 via-transparent to-transparent' : 'from-white/80 via-transparent to-transparent'}`} />
                  </motion.div>
                </AnimatePresence>
              </div>

            </div>
          </div>

          {/* Right Pane: Glassmorphic Interactive Details Panel */}
          <div className="lg:col-span-6 flex flex-col justify-center relative">

            {/* Header controls outside the AnimatePresence slider */}
            <div className="flex items-center justify-between mb-4 px-2 select-none">
              <span className={`text-[11px] uppercase font-poppins tracking-widest font-extrabold ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Service Focus: {currentCategoryData.label}
              </span>

              {/* Navigation Chevrons (Static, no unmounts!) */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  className={`w-9 h-9 rounded-full border flex items-center justify-center transition-all cursor-pointer shadow-sm ${isDark
                      ? 'border-white/10 bg-[#111827]/80 hover:bg-white/5 text-gray-400 hover:text-white'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900'
                    }`}
                  aria-label="Previous service focus"
                >
                  <ChevronLeft className="w-5 h-5 text-accent" />
                </button>
                <button
                  onClick={handleNext}
                  className={`w-9 h-9 rounded-full border flex items-center justify-center transition-all cursor-pointer shadow-sm ${isDark
                      ? 'border-white/10 bg-[#111827]/80 hover:bg-white/5 text-gray-400 hover:text-white'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900'
                    }`}
                  aria-label="Next service focus"
                >
                  <ChevronRight className="w-5 h-5 text-accent" />
                </button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeDetail.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className={`rounded-3xl p-6 sm:p-8 border flex flex-col justify-between h-full min-h-[460px] ${isDark
                    ? 'bg-[#111827]/80 border-white/[0.06] shadow-xl'
                    : 'bg-white border-black/[0.04] shadow-xl shadow-slate-100/50'
                  }`}
              >
                <div>

                  {/* Category Title & Badge */}
                  <div className="flex flex-wrap items-center gap-3.5 mb-4">
                    <span className="text-[10px] uppercase font-poppins tracking-wider font-extrabold px-3 py-1 bg-accent/15 border border-accent/25 rounded-md text-accent">
                      {activeDetail.badge}
                    </span>
                  </div>

                  {/* Main Header */}
                  <h3 className={`text-2xl sm:text-3xl lg:text-4xl font-poppins font-bold tracking-tight mb-3 ${isDark ? 'text-white' : 'text-primary'}`}>
                    {activeDetail.name}
                  </h3>

                  {/* Description Paragraph */}
                  <p className={`text-sm lg:text-base leading-relaxed mb-6 sm:mb-8 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {activeDetail.desc}
                  </p>

                  {/* Inclusions Grid */}
                  <div className="mb-6 sm:mb-8">
                    <h4 className={`text-xs lg:text-sm uppercase font-poppins font-bold tracking-widest mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      WHAT WE DELIVER
                    </h4>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                      {activeDetail.inclusions.map((inc) => (
                        <li key={inc} className="flex items-start gap-2.5 text-[13px] lg:text-[15px] leading-snug">
                          <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{inc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Execution Roadmap Timeline */}
                  <div className="mb-6 sm:mb-8">
                    <h4 className={`text-xs lg:text-sm uppercase font-poppins font-bold tracking-widest mb-3.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      EXECUTION ROADMAP
                    </h4>
                    <div className="grid grid-cols-3 gap-2.5 relative">
                      {activeDetail.timeline.map((step, idx) => (
                        <div key={step.step} className="relative z-10">
                          <div className={`text-[10px] lg:text-[11px] uppercase font-bold tracking-wider mb-1 font-poppins ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            STAGE {idx + 1}
                          </div>
                          <div className={`text-[12px] lg:text-[14px] font-bold leading-tight mb-1 truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {step.step}
                          </div>
                          <div className="text-[11px] lg:text-[12px] font-bold text-accent font-poppins flex items-center gap-1">
                            <Clock className="w-3 h-3 text-accent" />
                            {step.duration}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Lower Footer: Quick Specs & Direct CTA */}
                <div className="mt-4 pt-6 border-t border-gray-150/10 dark:border-gray-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">

                  {/* Metric Badges */}
                  <div className="flex items-center gap-4">
                    {activeDetail.metrics.slice(0, 2).map((met) => {
                      const MetIcon = met.icon;
                      return (
                        <div key={met.label} className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
                            <MetIcon className="w-4 h-4 text-accent" />
                          </div>
                          <div>
                            <div className={`text-[9px] lg:text-[10px] uppercase font-extrabold tracking-wider font-poppins ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {met.label}
                            </div>
                            <div className={`text-[12px] lg:text-[14px] font-bold ${isDark ? 'text-white' : 'text-gray-850'}`}>
                              {met.value}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Immediate CTA Button */}
                  <a
                    href="#consultation"
                    onClick={scrollToConsultation}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-accent hover:bg-accent-hover text-white text-xs uppercase font-poppins tracking-wider font-extrabold transition-all duration-300 shadow-md shadow-accent/15 transform hover:-translate-y-0.5 cursor-pointer"
                  >
                    Get Estimate
                    <ArrowRight className="w-4 h-4 text-white" />
                  </a>

                </div>

              </motion.div>
            </AnimatePresence>
          </div>

        </div>

      </div>
    </section>
  );
}

// Backward-compatibility export for AI system prompt knowledge base (src/constants/knowledge.ts)
export const SERVICES = [
  { title: 'Home Interiors', desc: '' },
  { title: 'Modular Kitchen', desc: '' },
  { title: 'Wardrobes & Storage', desc: '' },
  { title: 'Turnkey Projects', desc: '' }
];
